// TeamAdoption — how common a resource is within the requester's department:
// a labelled progress bar + "N of M members have access" + a tone chip. Counts
// only (no member identities).
// design ref: styles.css:1298 (.adoption / .adoption__head / .adoption__bar /
// .adoption__fill / .adoption__sub) + drawers.jsx TeamAdoption.

import { Box, Flex, Text } from "@chakra-ui/react";

import Chip, { type ChipTone } from "components/ds/Chip";
import type { MFTeamAdoption } from "state/members/tasks";

// ≥50% common, ≥20% limited, else rare — matches the prototype's thresholds.
const tier = (pct: number): { fill: string; tone: ChipTone; label: string } => {
  if (pct >= 50) return { fill: "success.500", tone: "success", label: "Common in this team" };
  if (pct >= 20) return { fill: "warning.500", tone: "warning", label: "Limited in this team" };
  return { fill: "error.500", tone: "error", label: "Rare in this team" };
};

export interface TeamAdoptionProps {
  data: MFTeamAdoption;
}

const TeamAdoption = ({ data }: TeamAdoptionProps) => {
  const { fill, tone, label } = tier(data.pct);

  return (
    <Flex direction="column" gap="1xs" w="100%">
      {/* .adoption__head */}
      <Flex justify="space-between" fontSize="smd">
        <Box as="span" color="neutral.700">
          Department ·{" "}
          <Box as="strong" color="neutral.900" fontWeight={600}>
            {data.group}
          </Box>
        </Box>
        <Box as="span" fontWeight={600} color="neutral.900">
          {data.pct}%
        </Box>
      </Flex>

      {/* .adoption__bar — 6px track, rounded, with a faint 50% gridline */}
      <Box position="relative" h="6px" bg="neutral.200" borderRadius="4px" overflow="hidden">
        <Box position="absolute" insetY={0} left={0} w={`${data.pct}%`} bg={fill} borderRadius="4px" />
        <Box position="absolute" insetY={0} left="50%" w="1px" bg="neutral.25" />
      </Box>

      {/* .adoption__sub */}
      <Flex gap="sm" align="center" fontSize="xs" color="neutral.700">
        <Box as="span">
          <Box as="strong" color="neutral.900" fontWeight={600}>
            {data.withAccess}
          </Box>{" "}
          of{" "}
          <Box as="strong" color="neutral.900" fontWeight={600}>
            {data.members}
          </Box>{" "}
          members have access
        </Box>
        <Chip tone={tone}>{label}</Chip>
      </Flex>
    </Flex>
  );
};

export default TeamAdoption;
